import { PaperClipOutlined } from "@ant-design/icons"
import { Button, Typography, Upload, message } from "antd"
import type { UploadFile } from "antd/es/upload/interface"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  RichTextEditor,
  type RichTextEditorHandle
} from "../../../../components/RichTextEditor/RichTextEditor.tsx"
import { RichTextViewer } from "../../../../components/RichTextEditor/RichTextViewer.tsx"
import { isRichTextEmpty } from "../../../../components/RichTextEditor/rich-text-html.ts"
import { WorkspaceCard } from "../../LeadWorkspacePage/cards/WorkspaceCard.tsx"
import {
  LEAD_UPLOAD_ACCEPT,
  attachmentName,
  leadUploadPatch,
  validateLeadUpload,
  visibleLeadAttachments
} from "../../LeadWorkspacePage/lead-attachments.ts"
import { type LeadPatchSender } from "../../LeadWorkspacePage/lead-workspace-patch.ts"
import { type LeadAttachment } from "../../types/lead.types.ts"

dayjs.extend(relativeTime)

const { Text } = Typography

interface InternalNotesValues {
  internal_notes: string | null
}

interface LeadViewInternalNotesProps {
  notes?: string | null
  attachments?: LeadAttachment[] | null
  canEdit?: boolean
  /** Two tabs may show this card at once; the guard registry keys on the id. */
  cardId?: string
  /** Opt-in only — lets a grid-stretched caller (Overview) match its sibling's height. */
  className?: string
  /** Larger on Overview than a narrow future caller might want. */
  minHeight?: number
  onPatch: LeadPatchSender
}

export function LeadViewInternalNotes({
  notes,
  attachments,
  canEdit,
  cardId = "internal-notes",
  className,
  minHeight = 220,
  onPatch
}: LeadViewInternalNotesProps) {
  const { t } = useTranslation()
  const title = t("leadWorkspace.cards.internalNotes.title")

  const editorRef = useRef<RichTextEditorHandle>(null)
  const [uploadingName, setUploadingName] = useState<string | null>(null)
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null)
  const [, forceTick] = useState(0)

  const visibleAttachments = useMemo(
    () => visibleLeadAttachments(attachments),
    [attachments]
  )

  const pendingUpload = useRef<{ name: string; priorIds: Set<string> } | null>(
    null
  )
  const latestAttachments = useRef(visibleAttachments)
  latestAttachments.current = visibleAttachments

  // Uploaded via this card during the edit session that's currently open —
  // cleared on save (they're referenced by the note now) and deleted on
  // Cancel/Discard (the note text that would have referenced them never
  // saved, so keeping the file would orphan it in the Files list forever).
  const sessionAttachmentIds = useRef<Set<string>>(new Set())

  // Refreshes the "last saved" relative-time label while it's on screen.
  useEffect(() => {
    if (lastSavedAt == null) return undefined
    const id = window.setInterval(() => forceTick((tick) => tick + 1), 30000)
    return () => window.clearInterval(id)
  }, [lastSavedAt])

  useEffect(() => {
    const pending = pendingUpload.current
    if (!pending) return

    const match = visibleAttachments.find(
      (file) =>
        !pending.priorIds.has(file.id) && attachmentName(file) === pending.name
    )
    if (!match) return

    editorRef.current?.insertAttachmentChip({
      id: match.id,
      name: attachmentName(match)
    })
    sessionAttachmentIds.current.add(match.id)
    pendingUpload.current = null
    setUploadingName(null)
  }, [visibleAttachments])

  const attachmentsById = useMemo(
    () => new Map(visibleAttachments.map((file) => [file.id, file])),
    [visibleAttachments]
  )

  const handleUpload = async (changed: UploadFile, fileList: UploadFile[]) => {
    const patch = leadUploadPatch(changed, fileList)
    if (!patch) return

    pendingUpload.current = {
      name: changed.name ?? "",
      priorIds: new Set((latestAttachments.current ?? []).map((f) => f.id))
    }
    setUploadingName(changed.name ?? null)

    const ok = await onPatch(patch)
    if (!ok) {
      pendingUpload.current = null
      setUploadingName(null)
      void message.error(t("richTextEditor.attachment.failed"))
    }
  }

  const attachButton = canEdit && (
    <Upload
      showUploadList={false}
      accept={LEAD_UPLOAD_ACCEPT}
      disabled={!!uploadingName}
      beforeUpload={validateLeadUpload}
      onChange={({ file, fileList }) => void handleUpload(file, fileList)}
    >
      <Button
        type={"text"}
        size={"small"}
        loading={!!uploadingName}
        icon={!uploadingName && <PaperClipOutlined />}
        aria-label={t("richTextEditor.toolbar.attach")}
      />
    </Upload>
  )

  return (
    <WorkspaceCard<InternalNotesValues>
      cardId={cardId}
      title={title}
      className={className}
      canEdit={canEdit}
      values={{ internal_notes: notes ?? null }}
      onSave={async (changed) => {
        const ok = await onPatch(changed)
        if (ok) {
          setLastSavedAt(Date.now())
          sessionAttachmentIds.current.clear()
        }
        return ok
      }}
      onCancel={() => {
        const ids = Array.from(sessionAttachmentIds.current)
        sessionAttachmentIds.current.clear()
        if (ids.length) void onPatch({ deleted_attachment_ids: ids })
      }}
      saveHint={
        lastSavedAt && (
          <Text type={"secondary"} className={"text-xs"}>
            {t("richTextEditor.lastSaved", {
              time: dayjs(lastSavedAt).fromNow()
            })}
          </Text>
        )
      }
      renderEdit={({ values, setValues, saving }) => (
        <div className={"flex flex-col gap-2"}>
          <RichTextEditor
            ref={editorRef}
            value={values.internal_notes ?? ""}
            onChange={(html) => setValues({ internal_notes: html })}
            placeholder={t("leadWorkspace.cards.internalNotes.placeholder")}
            ariaLabel={title}
            autoFocus
            disabled={saving}
            minHeight={minHeight}
            attachButton={attachButton}
          />
          {uploadingName && (
            <Text type={"secondary"} className={"text-xs"}>
              {t("richTextEditor.attachment.uploading", {
                name: uploadingName
              })}
            </Text>
          )}
        </div>
      )}
    >
      {({ startEdit }) =>
        isRichTextEmpty(notes) ? (
          <button
            type={"button"}
            onClick={canEdit ? startEdit : undefined}
            disabled={!canEdit}
            className={
              "w-full text-left rounded-md border border-dashed border-border px-3 py-4 text-gray-400 disabled:cursor-default"
            }
            data-testid={"workspace-card-empty"}
          >
            {t("leadWorkspace.cards.internalNotes.empty")}
          </button>
        ) : (
          <div
            tabIndex={0}
            role={"region"}
            aria-label={title}
            className={
              "tailwind-scroll overflow-y-auto overflow-x-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            }
            style={{ maxHeight: minHeight + 80 }}
          >
            <RichTextViewer
              html={notes}
              resolveAttachment={(id) => {
                const file = attachmentsById.get(id)
                return file ? { url: file.file, name: attachmentName(file) } : null
              }}
              unavailableLabel={t("richTextEditor.attachment.unavailable")}
            />
          </div>
        )
      }
    </WorkspaceCard>
  )
}
